/**
 * Display time (knowledge doc section 20: UTC on the wire and in storage, and the
 * dashboard is the only thing that converts).
 *
 * Every function takes the zone explicitly. Nothing falls back to the browser's,
 * because a fleet operator looking at a European server from a laptop still on New
 * York time needs the instance's answer rather than their laptop's.
 */

export interface DisplayOptions {
  /** The user's zone, or the instance default when they have not chosen one. */
  timezone: string;
}

/** The zone to render in: the user's choice, else the installation's default. */
export const effectiveTimezone = (userTimezone: string | null, instanceTimezone: string): string =>
  userTimezone ?? instanceTimezone;

const formatter = (timezone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
  try {
    return Intl.DateTimeFormat('en-GB', { ...options, timeZone: timezone });
  } catch {
    // A zone this browser does not know should not blank out a page; UTC is the
    // stored value anyway, so falling back to it shows the right instant.
    return Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' });
  }
};

/** `19 Sep 2026, 11:42` — short enough for a table cell, unambiguous about the day. */
export function formatInstant(iso: string, { timezone }: DisplayOptions): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return formatter(timezone, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(at);
}

/** The same instant with seconds, for anything where ordering matters. */
export function formatPreciseInstant(iso: string, { timezone }: DisplayOptions): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return formatter(timezone, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(at);
}

/** "3 minutes ago" — how long a server has been quiet reads better than a clock time. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';

  const seconds = Math.round((at.getTime() - now.getTime()) / 1000);
  const relative = new Intl.RelativeTimeFormat('en-GB', { numeric: 'auto' });

  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ['second', 60],
    ['minute', 60],
    ['hour', 24],
    ['day', 7],
    ['week', 4.348],
    ['month', 12],
  ];

  let value = seconds;
  for (const [unit, size] of steps) {
    if (Math.abs(value) < size) return relative.format(Math.round(value), unit);
    value /= size;
  }
  return relative.format(Math.round(value), 'year');
}
